<template>
  <div class="page-extension-counts">
    <h2 class="mb-0 mt-0">
      <v-svg class="voxo-green" sprite="telephone-2"></v-svg>
      Extension Counts
    </h2>
    <resize-observer @notify="__resizeHandler" />
    <div class="mt-5">
      <div class="flex justify-flex-end">
        <el-date-picker
          @change="doAdvanced"
          class="mb-10"
          style="margin-left: auto"
          v-model="historyDateRange"
          type="daterange"
          align="right"
          title="Filter"
          unlink-panels
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="datePickerOptions">
        </el-date-picker>
      </div>
      <extensions-plot ref="extensionsPlot" name="Extension Activity" :plot-data="extensionRecords"
        :loading="dataLoading" />
      <extension-records :records="extensionRecords" :loading="dataLoading" />
    </div>
  </div>
</template>

<script>
import ExtensionsPlot from "./components/extension-plot";
import ExtensionRecords from "./components/extension-records-table";
import feathersClient from "@/feathers/feathers-client";
import moment from "moment-timezone";

export default {
  name: "ExtensionPage",

  components: {
    ExtensionsPlot,
    ExtensionRecords
  },
  data() {
    return {
      resized: false,
      recordData: [],
      extensionRecords: [],
      dataLoading: false,
      plotData: [],
      advancedActive: false,
      filterDates: false,
      historyDateRange: [moment().subtract(30, 'days'), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ]
      },
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
    isResellerAccount() {
      return this.selectedTenant
        ? this.selectedTenant.isResellerAccount
        : false;
    },
    partnerType() {
      return this.selectedTenant ? this.selectedTenant.resellerType : false;
    }
  },

  watch: {
    user() {
      if (this.user) {
        this.findExtensions();
      }
    },
    selectedTenant() {
      this.findExtensions();
    }
  },

  methods: {
    __resizeHandler: _.throttle(function (e) {
      if (this.resized) {
        setTimeout(() => {
          this.$refs.extensionsPlot.chart.resize();
        }, 1000);
      }
      this.resized = true;
    }, 700),

    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },

    doAdvanced() {
      this.advancedActive = true;
      this.startDate = moment(this.historyDateRange[0]).format("YYYY-MM-DD");
      this.endDate = moment(this.historyDateRange[1]).format("YYYY-MM-DD");
      this.updateDateRangePicker();
      this.findExtensions(this.advancedActive);
      this.presetSelection = null;
    },

    getDatesInRange() {
      const dates = [moment(this.startDate).format("YYYY-MM-DD")];
      let currentDate = this.startDate;
      let endDate = moment(this.endDate).format("YYYY-MM-DD");
      while (currentDate < endDate) {
        const nextDay = moment(currentDate).add(1, "day");
        currentDate = nextDay.format("YYYY-MM-DD");
        dates.push(nextDay.format("YYYY-MM-DD"));
      }
      return dates;
    },

    findExtensions() {
      this.extensionRecords = [];
      this.dataLoading = true;
      feathersClient
        .service("billing")
        .find({
          query: {
            context: "extensionCounts",
            tenantId: this.selectedTenant.id,
            advancedActive: this.advancedActive,
            startDate: this.startDate,
            endDate: this.endDate
          }
        })
        .then(res => {
          this.extensionRecords = [...res.extensions];
          this.plotData = this.buildPlotData();
          this.dataLoading = false;
        })
        .catch(e => {
          this.dataLoading = false;
        });
    },

    buildPlotData() {
      const data = [];
      for (let i = 0; i < this.extensionRecords.length; i++) {
        const record = this.extensionRecords[i];
        const { day, extensions, partnerExtensions, partnerResidentialExtensions } = record;

        const plotObject = {
          day,
          extensions: extensions,
          partnerExtensions: partnerExtensions,
          partnerResidentialExtensions: partnerResidentialExtensions
        };

        data.push(plotObject);

      }
      return data;
    }
  },
  mounted() {
    if (this.user) {
      this.findExtensions();
    }
  }
};
</script>
<style lang="scss" scoped>
@import "../../../assets/scss/variables";

.page-extension-counts {
  overflow: scroll;
}

.extension-filter-button {
  float: right;
}

.filter-button {
  min-width: 100%;
  margin-bottom: 3rem;
}
</style>
